import React, { useContext, useState } from "react";
import {
  Box,
  Toolbar,
  AppBar,
  useTheme,
  useMediaQuery,
  IconButton,
  Button,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import { ThemeContext } from "../index";

/**
 * CustomNavbar
 *
 * Props:
 *   leftContent   — room code / round info
 *   centerContent — player name + role chip
 *   rightContent  — ONLINE DOT ONLY (always shown inline)
 *   menuItems     — array of { label, icon, onClick, color? }
 *                   Mobile: rendered inside hamburger menu
 *                   Desktop: rendered as inline text buttons
 */
const CustomNavbar = ({ leftContent, centerContent, rightContent, menuItems = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { mode, toggleTheme } = useContext(ThemeContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const themeToggle = (
    <IconButton
      onClick={toggleTheme}
      size="small"
      sx={{
        color: theme.palette.text.primary,
        transition: "transform 0.3s ease",
        "&:hover": { transform: "rotate(30deg)" },
      }}
    >
      {mode === "dark" ? (
        <LightModeIcon fontSize="small" />
      ) : (
        <DarkModeIcon fontSize="small" />
      )}
    </IconButton>
  );

  const barSx = {
    backgroundColor: alpha(theme.palette.background.paper, 0.75),
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    color: theme.palette.text.primary,
  };

  if (isMobile) {
    return (
      <AppBar position="sticky" sx={barSx}>
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 48,
            px: 1.5,
            gap: 0.5,
          }}
        >
          {/* Left: room code / round info */}
          <Box sx={{ flex: 1, overflow: "hidden", fontSize: "0.75rem" }}>
            {leftContent}
          </Box>

          {/* Center: player name + role */}
          <Box sx={{ flex: "0 1 auto", mx: 0.5 }}>{centerContent}</Box>

          {/* Right: online dot (always inline) + hamburger */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {rightContent}
            <IconButton
              size="small"
              onClick={handleOpen}
              sx={{ color: theme.palette.text.primary }}
              aria-label="menu"
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Hamburger menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: {
                mt: 0.5,
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: "blur(12px)",
                border: `1px solid ${theme.palette.divider}`,
                minWidth: 160,
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem onClick={() => { toggleTheme(); handleClose(); }} dense>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={mode === "dark" ? "Light Mode" : "Dark Mode"} />
            </MenuItem>

            {menuItems.length > 0 && <Divider sx={{ my: 0.5 }} />}

            {menuItems.map((item, i) => (
              <MenuItem
                key={i}
                onClick={() => { item.onClick(); handleClose(); }}
                dense
                sx={{ color: item.color || "inherit" }}
              >
                {item.icon && (
                  <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>
    );
  }

  // ===== DESKTOP =====
  return (
    <AppBar position="sticky" sx={barSx}>
      <Toolbar
        disableGutters
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 52,
          px: 2.5,
        }}
      >
        <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
          {leftContent}
        </Box>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", textAlign: "center" }}>
          {centerContent}
        </Box>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
          {rightContent}
          {/* Desktop: render menuItems as inline buttons */}
          {menuItems.map((item, i) => (
            <Button
              key={i}
              variant="text"
              size="small"
              startIcon={item.icon || null}
              onClick={item.onClick}
              sx={{
                fontWeight: 600,
                color: item.color || theme.palette.text.primary,
                textTransform: "none",
                fontSize: "0.8rem",
              }}
            >
              {item.label}
            </Button>
          ))}
          {themeToggle}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default CustomNavbar;
